(function() {
  'use strict';

  /* ============================================================
     ERROR TRACKING — fail-soft global handler.
     Logs to console always; pipes to GA / Shopify analytics if installed.
     ============================================================ */
  window.addEventListener('error', function(e) {
    try {
      const msg = '[zwim] ' + (e.message || 'error') + ' @ ' + (e.filename || '?') + ':' + (e.lineno || 0);
      console.error(msg, e.error);
      if (window.gtag) window.gtag('event', 'exception', { description: msg, fatal: false });
    } catch (_) { /* never throw from error handler */ }
  });
  window.addEventListener('unhandledrejection', function(e) {
    try {
      console.error('[zwim] unhandled promise rejection:', e.reason);
      if (window.gtag) window.gtag('event', 'exception', { description: 'Unhandled rejection: ' + (e.reason && e.reason.message || e.reason), fatal: false });
    } catch (_) { /* never throw */ }
  });

  /* ============================================================
     WAITLIST COUNTRY GEO-IP — pack visitor country into the form's TAG field.
     Full architecture: ~/chief-of-staff/memory/shopify-customer-country-bug.md

     Shopify's newsletter `{% form 'customer' %}` ignores contact[address][...]
     entirely. Tags ARE accepted. So we rewrite the tags field's placeholder
     `country-unknown` to `country-<ISO2>` using Cloudflare's same-origin
     /cdn-cgi/trace endpoint. A Shopify Flow then consumes the country-XX tag
     post-creation to update the customer's defaultAddress.countryCode.

     Failure mode: silent — tags stay as `waitlist,country-unknown` and Flow
     can route those for review. Better than a wrong country.
     ============================================================ */
  document.addEventListener('DOMContentLoaded', function() {
    fetch('/cdn-cgi/trace', { cache: 'no-store' })
      .then(function(r) { return r.text(); })
      .then(function(text) {
        var match = text.match(/^loc=([A-Z]{2})/m);
        if (!match) return;
        var code = match[1];
        document.querySelectorAll('input[name="contact[tags]"]')
          .forEach(function(el) {
            el.value = el.value.replace(/country-[A-Z]+|country-unknown/, 'country-' + code);
          });
      })
      .catch(function() { /* silent — country-unknown stays */ });
  });

  /* ============================================================
     ANALYTICS — platform-agnostic event dispatch.
     Anything that wants to track listens for the `zwim:track` event.
     Built-in handlers: window.dataLayer (GTM container GTM-M6K3ZR2M routes to GA4),
     window.gtag (GA4 direct fallback), Shopify's analytics.publish, and Meta Pixel (fbq).

     Meta Pixel mapping (ZWIM dataset 1922343125322437):
       sign_up   → CompleteRegistration   (with eventID for client/server dedup)
       cta_click → Lead
       view_content / live_feed_viewed → ViewContent
       share_*   → Contact (lightweight signal)

     eventID lets the future Conversions API server-side dedup its event vs the client one. {%- endcomment -%}
     ============================================================ */
  function genEventId(event) {
    return event + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 9);
  }
  // Map our event names → Meta standard event names. Anything not in this map is sent as a custom event.
  const FB_STANDARD = {
    sign_up: 'CompleteRegistration',
    cta_click: 'Lead',
    live_feed_viewed: 'ViewContent',
    share_whatsapp: 'Contact',
    share_native: 'Contact',
    share_copy: 'Contact'
  };
  function track(event, props) {
    props = props || {};
    const eventID = genEventId(event);
    try {
      // Custom DOM event — anyone can listen
      document.dispatchEvent(new CustomEvent('zwim:track', { detail: { event: event, props: props, eventID: eventID } }));
      // GTM dataLayer (also picks up by gtag-direct if installed standalone)
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: event, event_id: eventID }, props));
      }
      // GA4 direct (only if gtag is loaded standalone — GTM container has its own GA4 tag)
      if (typeof window.gtag === 'function') {
        window.gtag('event', event, Object.assign({ event_id: eventID }, props));
      }
      // Shopify built-in analytics
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, Object.assign({ event_id: eventID }, props));
      }
      // Meta Pixel — map our event → Meta standard event with eventID for CAPI dedup
      if (typeof window.fbq === 'function') {
        const fbEvent = FB_STANDARD[event];
        const fbPayload = Object.assign({}, props);
        if (fbEvent) {
          window.fbq('track', fbEvent, fbPayload, { eventID: eventID });
        } else {
          window.fbq('trackCustom', event, fbPayload, { eventID: eventID });
        }
      }
    } catch (_) { /* never throw from analytics */ }
    return eventID;
  }
  window.__zwimTrack = track;  // expose for any liquid section that wants to fire events

  /* ============================================================
     LAZY VIDEO LOADER — loads videos only when section approaches viewport.
     - Sets <source> + calls .load() + .play() when within 2 viewports.
     - Save-Data + slow-2g/2g: skip video, show poster only.
     ============================================================ */
  (function() {
    const videos = document.querySelectorAll('video.js-lazy-video');
    if (!videos.length || !('IntersectionObserver' in window)) {
      // Fallback: load immediately on legacy browsers without IO
      videos.forEach(function(v) { activateVideo(v); });
      return;
    }
    // Connection-aware: skip videos for users who explicitly opted into Save-Data,
    // OR who are on slow-2g/2g networks. Poster shows + a tap-to-play overlay lets them opt in.
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const skipVideo = conn && (conn.saveData === true || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g');
    if (skipVideo) {
      if (window.__zwimTrack) window.__zwimTrack('video_skipped_save_data', { effectiveType: conn.effectiveType, saveData: conn.saveData });
      videos.forEach(function(v) {
        v.setAttribute('controls', 'controls');  // user can tap to play if they want
        v.dataset.savedata = 'true';
      });
      return;
    }

    function activateVideo(v) {
      if (v.dataset.loaded === 'true') return;
      v.dataset.loaded = 'true';
      const src = v.dataset.src;
      if (!src) return;
      const source = document.createElement('source');
      source.src = src;
      source.type = 'video/mp4';
      v.appendChild(source);
      v.load();
      // play() returns a promise; ignore rejections (autoplay blocked etc.)
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(function() { /* user must tap */ });
    }

    const obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        if (e.isIntersecting) {
          activateVideo(e.target);
          obs.unobserve(e.target);
        }
      });
    }, { rootMargin: '200% 0px', threshold: 0 });

    videos.forEach(function(v) { obs.observe(v); });
  })();

  /* NAV — in-flow sticky bar (converted 30/06). Only toggles `.scrolled` past 40px.
     The old pill-era auto-hide (a `lastY` delta + a `.hidden` class toggle) was removed:
     a sticky header should stay visible, and Dawn's `.hidden{display:none!important}`
     would have collapsed the bar entirely. Dead `lastY`/`.hidden` remnants dropped 02/07. */
  const nav = document.getElementById('nav');
  if (nav) {
    // Clear any stale `.hidden` once at init (safety), then never touch it again.
    nav.classList.remove('hidden');
    let ticking = false;
    function updateNav() {
      if (window.scrollY > 40) nav.classList.add('scrolled'); else nav.classList.remove('scrolled');
      ticking = false;
    }
    window.addEventListener('scroll', function() {
      if (!ticking) { requestAnimationFrame(updateNav); ticking = true; }
    }, { passive: true });
  }

  /* HUD + MODAL ADAPTIVE THEME — scroll-driven, updates continuously.
     Formerly also toggled `.nav--light` on the nav, but zwim-v8-head.liquid
     deliberately neutralizes `.nav--light` (the bar is always near-black), so
     that toggle was a per-frame no-op — removed 02/07 (P2-2). This block now
     only drives the HUD background + modal context theme, which are still live. */
  (function() {
    const themed = document.querySelectorAll('[data-nav-theme]');
    if (!themed.length) return;
    const hudTop = 134;         // HUD's top edge (matches CSS `.hero__hud { top: 134px }`)
    const hudBottom = 250;      // HUD's bottom edge (incl. small buffer for taller content)
    function updateTheme() {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const centerProbe = vh / 2;  // for modal/context theme — probes viewport center
      let ctxCurrent = 'dark', hudHasLight = false;
      for (const s of themed) {
        const r = s.getBoundingClientRect();
        const theme = s.getAttribute('data-nav-theme') || 'dark';
        if (ctxCurrent === 'dark' && r.top <= centerProbe && r.bottom > centerProbe) ctxCurrent = theme;
        // HUD: hide if ANY light section's vertical range overlaps the HUD's region [134, 250].
        // This is precise overlap detection — beats single-point probes which miss boundary cases.
        if (!hudHasLight && theme === 'light' && r.top < hudBottom && r.bottom > hudTop) hudHasLight = true;
      }
      document.body.classList.toggle('zwim-hud-bg-light', hudHasLight);
      document.body.classList.toggle('zwim-modal-light', ctxCurrent === 'light');
    }
    updateTheme();
    window.addEventListener('scroll', function() {
      requestAnimationFrame(updateTheme);
    }, { passive: true });
    window.addEventListener('resize', updateTheme, { passive: true });
  })();

  /* REVEAL */
  const revObs = new IntersectionObserver(function(entries) {
    entries.forEach(function(e) {
      if (e.isIntersecting) { e.target.classList.add('vis'); revObs.unobserve(e.target); }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  /* GLASS PARALLAX — problem + metrics cards drift subtly on scroll */
  (function() {
    const cards = Array.from(document.querySelectorAll('.problem__card, .metrics__card'));
    if (!cards.length) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const entries = cards.map(function(el, idx) {
      const depth = 10 + (idx % 3) * 4;
      return { el: el, depth: depth, sign: (idx % 2 === 0 ? 1 : -1) };
    });
    let ticking = false;
    function update() {
      const vh = window.innerHeight || 800;
      for (const e of entries) {
        const r = e.el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) continue;
        const center = r.top + r.height / 2;
        const prog = (center - vh / 2) / (vh / 2 + r.height / 2);
        const ty = -prog * e.depth * e.sign;
        e.el.style.transform = 'translate3d(0,' + ty.toFixed(2) + 'px,0)';
      }
      ticking = false;
    }
    window.addEventListener('scroll', function() {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  })();
  document.querySelectorAll('.rv').forEach(function(el) { revObs.observe(el); });

  /* FAQ */
  document.querySelectorAll('.faq__q').forEach(function(q) {
    q.addEventListener('click', function() {
      const item = q.parentElement;
      const ans = item.querySelector('.faq__a');
      const open = item.classList.contains('active');
      document.querySelectorAll('.faq__item.active').forEach(function(o) {
        o.classList.remove('active');
        o.querySelector('.faq__a').style.maxHeight = '0';
        o.querySelector('.faq__q').setAttribute('aria-expanded', 'false');
      });
      if (!open) {
        item.classList.add('active');
        ans.style.maxHeight = ans.scrollHeight + 'px';
        q.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* MODAL — click-triggered waitlist modal (Shopify Forms handles exit-intent) */
  (function() {
    const modal = document.getElementById('zwim-waitlist-modal');
    if (!modal) return;
    let lastFocus = null;

    // Theme is kept current live by the scroll handler (body.zwim-modal-light toggles continuously).
    // No open-time detection needed — modal reads whatever the current theme is.
    const formState = modal.querySelector('.zwim-modal__state--form');
    const successState = modal.querySelector('.zwim-modal__state--success');

    function resetStates() {
      if (formState) formState.hidden = false;
      if (successState) successState.hidden = true;
    }
    function spawnConfetti() {
      const container = modal.querySelector('#zwim-confetti');
      if (!container) return;
      container.innerHTML = '';
      const COLORS = ['#78FAFC', '#E93B68', '#FFFFFF', '#78FAFC', '#E93B68'];
      for (let i = 0; i < 32; i++) {
        const p = document.createElement('i');
        const angle = (Math.random() * 360) * (Math.PI / 180);
        const distance = 90 + Math.random() * 130;
        const dx = Math.cos(angle) * distance;
        const dy = Math.sin(angle) * distance;
        const rot = -540 + Math.random() * 1080;
        p.style.setProperty('--dx', dx.toFixed(0) + 'px');
        p.style.setProperty('--dy', dy.toFixed(0) + 'px');
        p.style.setProperty('--rot', rot.toFixed(0) + 'deg');
        p.style.background = COLORS[i % COLORS.length];
        p.style.animationDelay = (Math.random() * 0.25).toFixed(2) + 's';
        container.appendChild(p);
      }
      setTimeout(function() { if (container) container.innerHTML = ''; }, 2400);
    }

    function showSuccess() {
      if (formState) formState.hidden = true;
      if (successState) successState.hidden = false;
      spawnConfetti();
      // Stay open until the user dismisses (X / backdrop / ESC). The success state hosts the
      // WhatsApp share section — we want them to read it and share, not be rushed out.
    }

    function openModal(triggerEl) {
      lastFocus = triggerEl || document.activeElement;
      resetStates();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('zwim-modal-open');
      const input = modal.querySelector('input[type="email"]');
      if (input) setTimeout(function() { input.focus(); }, 80);
    }
    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('zwim-modal-open');
      // Reset back to form state for the next open
      setTimeout(resetStates, 300);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    // Expose modal helpers globally so any liquid section / external form can trigger
    // the same success-state experience (confetti + EARLY ACCESS badge + share section).
    // Usage: window.__zwimOpenModalSuccess(emailString) — opens modal and goes straight to success.
    window.__zwimOpenModalSuccess = function(email) {
      openModal(null);
      showSuccess();
      // Identity tracking — same flow as inline modal submit
      if (email && typeof window.gtag === 'function') {
        try { window.gtag('set', 'user_data', { email: email.trim().toLowerCase() }); } catch (_) {}
      }
      if (email && typeof window.fbq === 'function') {
        try { window.fbq('init', '1922343125322437', { em: email.trim().toLowerCase() }); } catch (_) {}
      }
      if (window.__zwimTrack) {
        window.__zwimTrack('sign_up', { method: 'waitlist', source: 'footer', has_email: !!email });
        window.__zwimTrack('modal_submit_success', { source: 'footer', has_email: !!email });
      }
    };

    // MODAL form submission — DELIBERATELY native, NOT AJAX-intercepted.
    //
    // Why: Shopify's bot protection (reCAPTCHA on customer forms) attaches a
    // submit-event listener that injects a captcha token into the form just before
    // it posts. If we e.preventDefault() and AJAX-submit, Shopify's listener never
    // runs → no token → /contact returns "Missing CAPTCHA token" and the customer
    // is NEVER created. That was the bug DevOps reported on 8 May 2026.
    //
    // Native submit reloads the page after the customer is created (Shopify redirects
    // back to the source page with form.posted_successfully? = true). The success
    // celebration is then re-shown in the modal via the page-load detector below.
    // Save scroll position before ANY waitlist form submits, so we can restore it after
    // the page reload (Shopify customer create = full POST + redirect). Apply to all .wl
    // forms on the page (modal, capture, footer).
    document.querySelectorAll('form.wl').forEach(function(f) {
      f.addEventListener('submit', function() {
        try {
          sessionStorage.setItem('zwim_pending_scroll', String(window.scrollY || 0));
          sessionStorage.setItem('zwim_pending_signup', '1');
        } catch (_) {}
      }, true); // capture phase — runs before any other handler
    });

    const modalForm = modal.querySelector('form.wl');
    if (modalForm) {
      // Track the submit attempt for funnel analytics. We do NOT preventDefault —
      // the form posts natively and Shopify\'s captcha + customer-create flow runs.
      modalForm.addEventListener('submit', function() {
        const input = modalForm.querySelector('input[type="email"]');
        const email = (input && input.value) ? input.value.trim().toLowerCase() : null;
        if (email && typeof window.gtag === 'function') {
          try { window.gtag('set', 'user_data', { email: email }); } catch (_) {}
        }
        if (email && typeof window.fbq === 'function') {
          try { window.fbq('init', '1922343125322437', { em: email }); } catch (_) {}
        }
        if (window.__zwimTrack) {
          window.__zwimTrack('sign_up', { method: 'waitlist', has_email: !!email });
          window.__zwimTrack('modal_submit_attempt', { has_email: !!email });
        }
        const btn = modalForm.querySelector('.wl__btn');
        if (btn) { btn.textContent = '\u2713 Joining\u2026'; }
      }, true);
    }

    // Page-load detector: if a customer form was just submitted successfully and the
    // user landed back here, the form\'s Liquid block renders ".wl__success" inline.
    // Restore the user\'s pre-submit scroll position FIRST (so they end up where they
    // were, not at the page top), then auto-open the modal in success state. The user
    // closes the modal via X — they remain at the section they submitted from.
    if (document.querySelector('form.wl .wl__success')) {
      // Disable browser auto-scroll-restoration so Shopify\'s redirect-with-fragment
      // can\'t override the position we want.
      try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (_) {}

      let savedScroll = 0;
      try { savedScroll = parseInt(sessionStorage.getItem('zwim_pending_scroll') || '0', 10) || 0; } catch (_) {}
      if (savedScroll > 0) {
        // Instant scroll — happens before paint so user never sees the page-top flash
        try { window.scrollTo({ top: savedScroll, left: 0, behavior: 'instant' }); }
        catch (_) { window.scrollTo(0, savedScroll); }
      }

      setTimeout(function() {
        if (typeof window.__zwimOpenModalSuccess === 'function') {
          window.__zwimOpenModalSuccess();
        }
      }, 80);

      try {
        sessionStorage.removeItem('zwim_pending_scroll');
        sessionStorage.removeItem('zwim_pending_signup');
      } catch (_) {}
    }

    // Open on any .js-waitlist-trigger click (nav / hero / experience / leaderboard CTAs).
    // Capture phase + stopImmediatePropagation so the smooth-anchor handler below doesn't also fire.
    document.addEventListener('click', function(e) {
      const trigger = e.target.closest('.js-waitlist-trigger');
      if (trigger) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        // Analytics: which CTA was clicked (closest section's data-screen-label or fallback)
        const sectionEl = trigger.closest('[data-screen-label]');
        if (window.__zwimTrack) window.__zwimTrack('cta_click', {
          source: sectionEl ? sectionEl.getAttribute('data-screen-label') : 'unknown',
          label: trigger.textContent.trim().substring(0, 40)
        });
        if (window.__zwimTrack) window.__zwimTrack('xp_earned', { amount: 250, source: 'cta_click' });
        // Phase 3: XP boost on click — fires before opening the modal
        if (typeof window.__zwimPopXp === 'function') window.__zwimPopXp(250);
        openModal(trigger);
        if (window.__zwimTrack) window.__zwimTrack('modal_open', { trigger: 'cta' });
        return;
      }
    }, true);
    // Close handlers (bubble phase is fine here)
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-modal-close]')) {
        e.preventDefault();
        if (modal.classList.contains('is-open') && window.__zwimTrack) window.__zwimTrack('modal_close', {});
        closeModal();
      }
    });
    // Close on ESC
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    /* ===== Share handlers — WhatsApp primary, native share / clipboard fallback.
       Both buttons fire +250 XP via window.__zwimPopXp (every click — sharing is a real
       cost, every successful one earns the bump). */
    // UTM-tagged share URLs — short codes per channel. GA4 auto-captures source/medium
    // on the recipient's first pageview. Reports show: Acquisition → "wa / share", "nat / share", "cpy / share".
    const SHARE_BASE = 'https://zwim.com';
    const SHARE_URL_WA  = SHARE_BASE + '?utm_source=wa&utm_medium=share';   // WhatsApp
    const SHARE_URL_NAT = SHARE_BASE + '?utm_source=nat&utm_medium=share';  // Native share sheet (iMessage/Telegram/etc)
    const SHARE_URL_CPY = SHARE_BASE + '?utm_source=cpy&utm_medium=share';  // Copy-to-clipboard
    function shareText(url) {
      // 13/08: was "Power Paddles", which is not a product. Peer-to-peer message, so it
      // complements the link's OG card rather than repeating its headline. "Soon" not a
      // date on purpose: this sits in someone's chat history forever and the pre-order
      // date has already moved twice.
      return '🏊 15 minutes at home between pool days. ZWIM Smart Paddles read every stroke, the data\'s live on screen, and it\'s a game so you actually keep going. Pre-orders open soon: ' + url;
    }

    function showShareToast(msg, sourceEl) {
      // Scope toast to the SAME share section as the clicked button (modal AND footer have their own).
      const parent = sourceEl ? sourceEl.closest('.zwim-share') : modal.querySelector('.zwim-share');
      const toast = parent ? parent.querySelector('[data-share-toast]') : null;
      if (!toast) return;
      const txt = toast.querySelector('.zwim-share__toast-text');
      if (txt) txt.textContent = msg;
      toast.hidden = false;
      // Force reflow so the transition plays from initial state
      // eslint-disable-next-line no-unused-expressions
      toast.offsetHeight;
      toast.classList.add('is-visible');
      setTimeout(function() {
        toast.classList.remove('is-visible');
        setTimeout(function() { toast.hidden = true; }, 320);
      }, 1700);
    }
    function fireShareXp(channel) {
      if (typeof window.__zwimPopXp === 'function') window.__zwimPopXp(250);
      if (window.__zwimTrack) window.__zwimTrack('xp_earned', { amount: 250, source: 'share' });
      if (window.__zwimTrack && channel) window.__zwimTrack('share_' + channel, {});
    }

    // WhatsApp share — opens wa.me with pre-filled message + WhatsApp-tagged URL.
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-share-whatsapp]')) {
        e.preventDefault();
        fireShareXp('whatsapp');
        const url = 'https://wa.me/?text=' + encodeURIComponent(shareText(SHARE_URL_WA));
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    });

    // Native share / clipboard fallback — opens system share sheet on mobile (iMessage,
    // Telegram, Signal, etc.); copies to clipboard on desktop without share API.
    // URL is tagged differently per channel so GA4 can split native vs copy attribution.
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-share-native]')) {
        e.preventDefault();
        if (navigator.share) {
          fireShareXp('native');
          navigator.share({ text: shareText(SHARE_URL_NAT), url: SHARE_URL_NAT }).catch(function() { /* user cancelled */ });
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          fireShareXp('copy');
          const btn = e.target.closest('[data-share-native]');
          navigator.clipboard.writeText(shareText(SHARE_URL_CPY)).then(function() {
            showShareToast('Link copied', btn);
          }).catch(function() {
            showShareToast('Copy failed', btn);
          });
        } else {
          showShareToast('Copy not supported', e.target.closest('[data-share-native]'));
        }
      }
    });
  })();

  /* FORM — every non-modal .wl form (footer + capture section) AJAX-submits and triggers the
     SAME success experience as the modal: confetti + EARLY ACCESS badge + share section.
     Skip the modal form (it has its own handler with `data-modal-ajax="true"`).
     This is what unifies all signup paths: regardless of where you submit, you see the same flow. */
  document.querySelectorAll('.wl').forEach(function(form) {
    if (form.dataset.modalAjax === 'true') return;
    form.addEventListener('submit', function(e) {
      const btn = form.querySelector('.wl__btn');
      const input = form.querySelector('input[type="email"]');
      if (!input || !input.value) return;  // browser shows native required validation
      e.preventDefault();
      e.stopPropagation();
      const email = input.value.trim().toLowerCase();
      if (btn) {
        btn.classList.add('is-success');
        btn.textContent = '\u2713 Joining\u2026';
        btn.disabled = true;
      }
      const fd = new FormData(form);
      fetch(form.action || '/contact', { method: 'POST', body: fd })
        .then(function(response) {
          /* Only celebrate a real success. Shopify returns a redirect (followed by fetch) on
             success; a captcha challenge redirects to /challenge; errors return 4xx/5xx.
             Previously ANY response (422/429/500/challenge) showed "You're in" \u2014 signups
             could be silently lost while the UI confirmed them. Fixed 11/06/2026. */
          if (!response.ok || (response.url && response.url.indexOf('challenge') !== -1)) {
            throw new Error('signup-not-confirmed');
          }
          // Trigger modal success state with email passed for analytics + Advanced Matching
          if (typeof window.__zwimOpenModalSuccess === 'function') {
            window.__zwimOpenModalSuccess(email);
          }
          // Reset button after a moment so the form doesn't look stuck if user re-uses it
          setTimeout(function() {
            if (btn) {
              btn.classList.remove('is-success');
              btn.disabled = false;
              btn.textContent = btn.dataset.origLabel || '\u2713 Joined';
            }
            input.value = '';
          }, 1200);
        })
        .catch(function() {
          /* Fall back to a native (non-AJAX) submit so Shopify's captcha flow can run \u2014
             the customer still gets signed up, just without the confetti. */
          if (btn) {
            btn.classList.remove('is-success');
            btn.disabled = false;
            btn.textContent = btn.dataset.origLabel || 'Join the waitlist';
          }
          /* form.submit() bypasses JS submit handlers — no loop risk. */
          form.dataset.nativeFallback = 'true';
          form.submit();
        });
    });
  });

  /* COUNTERS */
  const cObs = new IntersectionObserver(function(entries) {
    entries.forEach(function(e) {
      if (!e.isIntersecting) return;
      const el = e.target;
      const target = parseFloat(el.getAttribute('data-count'));
      const pre = el.getAttribute('data-prefix') || '';
      const suf = el.getAttribute('data-suffix') || '';
      const dec = parseInt(el.getAttribute('data-decimals') || '0');
      const dur = 1800;
      let start = null;
      function ease(t) { return 1 - Math.pow(1 - t, 3); }
      function step(ts) {
        if (!start) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        const v = ease(p) * target;
        el.textContent = pre + (dec > 0 ? v.toFixed(dec) : Math.round(v)) + suf;
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
      cObs.unobserve(el);
    });
  }, { threshold: 0.5 });
  document.querySelectorAll('[data-count]').forEach(function(el) { cObs.observe(el); });

  /* SMOOTH ANCHORS */
  document.querySelectorAll('a[href^="#"]').forEach(function(a) {
    a.addEventListener('click', function(e) {
      const t = document.querySelector(a.getAttribute('href'));
      if (t && a.getAttribute('href') !== '#') {
        e.preventDefault();
        window.scrollTo({ top: t.offsetTop - 60, behavior: 'smooth' });
      }
    });
  });

  /* LIVE HUD B1.1 — drives all triptych fields + legacy IDs (for leaderboard sync) */
  (function() {
    // New triptych elements
    const zpwEl    = document.getElementById('hud-zpw');
    const zpwCyan  = document.getElementById('hud-zpw-cyan');
    const zpwPink  = document.getElementById('hud-zpw-pink');
    const zpwTick  = document.querySelector('.hud-hero-bar > .hud-hero-bar__tick');
    const totalEl  = document.getElementById('hud-total');
    const legendProp = document.getElementById('hud-legend-prop');
    const legendEff  = document.getElementById('hud-legend-eff');
    const legendUnlock = document.getElementById('hud-legend-unlock');
    const hrEl     = document.getElementById('hud-hr');
    const zoneEl   = document.getElementById('hud-zone');
    const spmEl    = document.getElementById('hud-spm');
    const lrLEl    = document.getElementById('hud-lr-l');
    const lrREl    = document.getElementById('hud-lr-r');
    const lrLBar   = document.getElementById('hud-lr-bar-l');
    const lrRBar   = document.getElementById('hud-lr-bar-r');
    const distEl   = document.getElementById('hud-dist');
    const timeEl   = document.getElementById('hud-time');
    const paceEl   = document.getElementById('hud-pace');
    // Legacy hidden IDs (still referenced by leaderboard climb logic)
    const legacyPw    = document.getElementById('hud-pw');
    const legacyPaceM = document.getElementById('hud-pace-m');
    const legacyPaceS = document.getElementById('hud-pace-s');
    const legacyPaceT = document.getElementById('hud-pace-t');
    const legacyWasted = document.getElementById('hud-wasted');
    const legacyTimer = document.getElementById('hud-timer');
    if (!zpwEl) return;

    // Initial state — physics: totalW (95-110) and efficiency (45-54%) drive ZPW.
    //   ZPW = totalW × efficiency / 100  →  range 43-59W (matches the design's "56 ZPW · 110W total")
    //   Pace correlates with efficiency (faster pace at higher efficiency)
    let totalW = 105;      // total mechanical watts (95-110)
    let eff = 50;          // efficiency 45-54%
    let hr = 132;          // 128-136 bpm
    let spm = 58;          // 54-62 strokes per minute
    let lrPhase = 0;       // sine phase for L/R oscillation
    let distM = 0;         // distance accumulator (starts 0 on page load)
    const startMs = Date.now();
    let lastT = performance.now();

    function fmtTime(seconds) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds - m * 60);
      return m.toString().padStart(2, '0') + ':' + s.toString().padStart(2, '0');
    }

    // ---- TICK GATING — pause when tab hidden, when HUD off-screen, or reduced-motion is on
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hudEl = document.querySelector('.hero__hud');
    let hudInView = true;   // optimistic — IO will correct
    if ('IntersectionObserver' in window && hudEl) {
      const hudVizObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(e) { hudInView = e.isIntersecting; });
      }, { rootMargin: '50% 0px', threshold: 0 });
      hudVizObs.observe(hudEl);
    }
    function shouldTick() {
      if (reducedMotion) return false;
      if (document.visibilityState !== 'visible') return false;
      if (!hudInView) return false;
      return true;
    }

    // Time ticker (1s interval, dedicated) — gated
    function updateTime() {
      const elapsed = (Date.now() - startMs) / 1000;
      const txt = fmtTime(elapsed);
      if (timeEl) timeEl.textContent = txt;
      if (legacyTimer) legacyTimer.textContent = txt;
    }
    updateTime();
    setInterval(function() { if (shouldTick()) updateTime(); }, 1000);

    // Main tick — drives the live values + visual updates
    function tick() {
      // Total mechanical watts — drift 95-110
      totalW += (Math.random() - 0.5) * 2.4;
      if (totalW < 95)  totalW = 95 + Math.random();
      if (totalW > 110) totalW = 110 - Math.random();
      // Efficiency drift 45-54%
      eff += (Math.random() - 0.5) * 1.4;
      if (eff < 45) eff = 45 + Math.random();
      if (eff > 54) eff = 54 - Math.random();
      const totalR = Math.round(totalW);
      const effR = Math.round(eff);
      // ZPW = totalW × efficiency / 100 (this is the actual physics)
      const zpwR = Math.round((totalR * effR) / 100);
      const unlockR = totalR - zpwR;
      const pct = zpwR / totalR;

      if (zpwEl)    zpwEl.textContent    = zpwR;
      if (totalEl)  totalEl.textContent  = totalR;
      // Flex-basis on each half of the cyan/pink bar — sum is 100%, tick is fixed 2px.
      // Also set explicit width — belt+suspenders, in case flex resolves to content-size.
      if (zpwCyan)  {
        zpwCyan.style.flex  = '0 0 ' + (pct * 100).toFixed(1) + '%';
        zpwCyan.style.width = (pct * 100).toFixed(1) + '%';
      }
      if (zpwPink)  {
        zpwPink.style.flex  = '1 1 ' + ((1 - pct) * 100).toFixed(1) + '%';
        zpwPink.style.width = ((1 - pct) * 100).toFixed(1) + '%';
      }
      if (zpwTick) {
        zpwTick.style.left = (pct * 100).toFixed(1) + '%';
      }
      if (legendProp)   legendProp.textContent   = zpwR;
      if (legendEff)    legendEff.textContent    = effR;
      if (legendUnlock) legendUnlock.textContent = unlockR;

      // Heart rate 128-136 (gentle drift)
      hr += (Math.random() - 0.5) * 1.6;
      if (hr < 128) hr = 128 + Math.random();
      if (hr > 136) hr = 136 - Math.random();
      const hrR = Math.round(hr);
      if (hrEl) hrEl.textContent = hrR;
      // Zone color flips Z2 (green, <135) → Z3 (cyan, ≥135)
      if (zoneEl) {
        if (hrR >= 135) {
          zoneEl.textContent = 'Z3';
          zoneEl.classList.remove('hud-zone--z2');
          zoneEl.classList.add('hud-zone--z3');
        } else {
          zoneEl.textContent = 'Z2';
          zoneEl.classList.remove('hud-zone--z3');
          zoneEl.classList.add('hud-zone--z2');
        }
      }

      // SPM 54-62
      spm += (Math.random() - 0.5) * 1.2;
      if (spm < 54) spm = 54 + Math.random();
      if (spm > 62) spm = 62 - Math.random();
      if (spmEl) spmEl.textContent = Math.round(spm);

      // L/R balance — sine oscillation between 48/52 and 52/48 (period ~14 seconds)
      lrPhase += 0.06;  // ~14s period at 900ms interval
      const lrL = Math.round(50 + Math.sin(lrPhase) * 2);
      const lrR = 100 - lrL;
      if (lrLEl) lrLEl.textContent = lrL;
      if (lrREl) lrREl.textContent = lrR;
      if (lrLBar) {
        lrLBar.style.flex  = '0 0 ' + lrL + '%';
        lrLBar.style.width = lrL + '%';
      }
      if (lrRBar) {
        lrRBar.style.flex  = '1 1 ' + lrR + '%';
        lrRBar.style.width = lrR + '%';
      }

      // Pace — 1:28-1:36 correlated with efficiency (higher eff → faster pace).
      // Map eff 45→96s (1:36), eff 54→88s (1:28).
      const paceMin = 88;     // seconds at eff 54%
      const paceMax = 96;     // seconds at eff 45%
      const effPct = (effR - 45) / 9; // 0..1
      const paceTot = paceMax - effPct * (paceMax - paceMin) + (Math.random() - 0.5) * 0.5;
      const m = Math.floor(paceTot / 60);
      const sFull = paceTot - m * 60;
      const sInt = Math.floor(sFull);
      const sTen = Math.floor((sFull - sInt) * 10);
      if (paceEl) paceEl.textContent = m + ':' + sInt.toString().padStart(2, '0');

      // Distance — accumulates from page load based on live pace
      const now = performance.now();
      const dt = Math.min((now - lastT) / 1000, 2);
      lastT = now;
      distM += (100 / paceTot) * dt;
      if (distEl) distEl.textContent = Math.round(distM).toLocaleString();

      // Legacy IDs (drive existing leaderboard climb / live-sync code)
      if (legacyPw)     legacyPw.textContent     = zpwR;
      if (legacyPaceM)  legacyPaceM.textContent  = m;
      if (legacyPaceS)  legacyPaceS.textContent  = sInt.toString().padStart(2, '0');
      if (legacyPaceT)  legacyPaceT.textContent  = sTen;
      if (legacyWasted) legacyWasted.textContent = effR + '%';
    }
    tick();
    // Gated tick — skips work when tab hidden, HUD off-screen, or reduced-motion preferred
    setInterval(function() { if (shouldTick()) tick(); }, 900);
  })();

  /* +XP POP CHIP — top-right above HUD. Fires on waitlist CTA click + WhatsApp share + bottom milestone.
     Signature: __zwimPopXp(amount, label, durationMs)
       - amount: numeric, default 250
       - label: text after "XP" (default "UNLOCKED"; passing anything else applies .is-milestone)
       - durationMs: how long the chip stays before fading out (default 2300) */
  (function() {
    const xpPop   = document.getElementById('zwim-xp-pop');
    const popText = document.getElementById('xp-pop-text');
    if (!xpPop) return;
    if (window.innerWidth < 900) { xpPop.style.display = 'none'; return; }

    let popHideTimer = null;
    window.__zwimPopXp = function(amount, label, durationMs) {
      amount = amount || 250;
      label = label || 'UNLOCKED';
      durationMs = durationMs || 2300;
      if (popText) popText.textContent = '+' + amount + ' XP ' + label;
      // .is-milestone applies stronger glow + slightly larger sizing for the bottom-of-page moment
      xpPop.classList.toggle('is-milestone', label !== 'UNLOCKED');
      // Force-restart the entrance animation if a previous pop is mid-flight
      xpPop.classList.remove('is-visible');
      void xpPop.offsetWidth;
      xpPop.classList.add('is-visible');
      xpPop.setAttribute('aria-hidden', 'false');
      if (popHideTimer) clearTimeout(popHideTimer);
      popHideTimer = setTimeout(function() {
        xpPop.classList.remove('is-visible');
        xpPop.setAttribute('aria-hidden', 'true');
      }, durationMs);
    };
  })();

  /* LIVE FEED (mobile section) — drives the FULL B1.1 HUD overlay over the gameplay video.
     1:1 mirror of the desktop B1.1 physics + fields: totalW (95-110), eff (45-54%),
     ZPW = totalW * eff / 100, HR (128-136 with Z2/Z3 zone), SPM (54-62), L/R sine drift,
     pace correlated with eff, distance accumulator, power legend, total mechanical.
     Uses live-hud-* IDs so it runs independently of the desktop HUD. */
  (function() {
    const section = document.querySelector('.live-feed');
    if (!section) return;

    // ---- TICK GATING — pause when tab hidden, when section off-screen, or reduced-motion is on
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let sectionInView = false;
    let liveFeedViewedFired = false;
    function shouldTick() {
      if (reducedMotion) return false;
      if (document.visibilityState !== 'visible') return false;
      if (!sectionInView) return false;
      return true;
    }

    // Reveal chip + You pill + HUD when section enters viewport. Also drives tick gating.
    const obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        sectionInView = e.isIntersecting;
        if (e.isIntersecting && e.intersectionRatio > 0.20) {
          section.classList.add('is-visible');
          if (!liveFeedViewedFired && window.__zwimTrack) {
            liveFeedViewedFired = true;
            window.__zwimTrack('live_feed_viewed', {});
          }
        }
      });
    }, { rootMargin: '50% 0px', threshold: [0, 0.20, 0.5] });
    obs.observe(section);

    // ---- Triptych elements (mirror desktop B1.1)
    const zpwEl       = document.getElementById('live-hud-zpw');
    const zpwCyan     = document.getElementById('live-hud-zpw-cyan');
    const zpwPink     = document.getElementById('live-hud-zpw-pink');
    const totalEl     = document.getElementById('live-hud-total');
    const legendProp  = document.getElementById('live-hud-legend-prop');
    const legendEff   = document.getElementById('live-hud-legend-eff');
    const legendUnlock = document.getElementById('live-hud-legend-unlock');
    const hrEl        = document.getElementById('live-hud-hr');
    const zoneEl      = document.getElementById('live-hud-zone');
    const spmEl       = document.getElementById('live-hud-spm');
    const lrLEl       = document.getElementById('live-hud-lr-l');
    const lrREl       = document.getElementById('live-hud-lr-r');
    const lrLBar      = document.getElementById('live-hud-lr-bar-l');
    const lrRBar      = document.getElementById('live-hud-lr-bar-r');
    const distEl      = document.getElementById('live-hud-dist');
    const timeEl      = document.getElementById('live-hud-time');
    const paceEl      = document.getElementById('live-hud-pace');
    if (!zpwEl) return;

    // ---- Initial state (matches desktop)
    let totalW = 105;
    let eff = 50;
    let hr = 132;
    let spm = 58;
    let lrPhase = 0;
    let distM = 0;
    const startMs = Date.now();
    let lastT = performance.now();

    function fmtTime(seconds) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds - m * 60);
      return m.toString().padStart(2, '0') + ':' + s.toString().padStart(2, '0');
    }
    setInterval(function() {
      if (!shouldTick()) return;
      if (timeEl) timeEl.textContent = fmtTime((Date.now() - startMs) / 1000);
    }, 1000);

    function tick() {
      // Total mechanical watts — drift 95-110
      totalW += (Math.random() - 0.5) * 2.4;
      if (totalW < 95)  totalW = 95 + Math.random();
      if (totalW > 110) totalW = 110 - Math.random();
      // Efficiency drift 45-54%
      eff += (Math.random() - 0.5) * 1.4;
      if (eff < 45) eff = 45 + Math.random();
      if (eff > 54) eff = 54 - Math.random();
      const totalR = Math.round(totalW);
      const effR = Math.round(eff);
      const zpwR = Math.round((totalR * effR) / 100);
      const unlockR = totalR - zpwR;
      const pct = zpwR / totalR;

      if (zpwEl)        zpwEl.textContent = zpwR;
      if (totalEl)      totalEl.textContent = totalR;
      if (zpwCyan) {
        zpwCyan.style.flex  = '0 0 ' + (pct * 100).toFixed(1) + '%';
        zpwCyan.style.width = (pct * 100).toFixed(1) + '%';
      }
      if (zpwPink) {
        zpwPink.style.flex  = '1 1 ' + ((1 - pct) * 100).toFixed(1) + '%';
        zpwPink.style.width = ((1 - pct) * 100).toFixed(1) + '%';
      }
      if (legendProp)   legendProp.textContent   = zpwR;
      if (legendEff)    legendEff.textContent    = effR;
      if (legendUnlock) legendUnlock.textContent = unlockR;

      // HR 128-136
      hr += (Math.random() - 0.5) * 1.6;
      if (hr < 128) hr = 128 + Math.random();
      if (hr > 136) hr = 136 - Math.random();
      const hrR = Math.round(hr);
      if (hrEl) hrEl.textContent = hrR;
      if (zoneEl) {
        if (hrR >= 135) {
          zoneEl.textContent = 'Z3';
          zoneEl.classList.remove('hud-zone--z2');
          zoneEl.classList.add('hud-zone--z3');
        } else {
          zoneEl.textContent = 'Z2';
          zoneEl.classList.remove('hud-zone--z3');
          zoneEl.classList.add('hud-zone--z2');
        }
      }

      // SPM 54-62
      spm += (Math.random() - 0.5) * 1.2;
      if (spm < 54) spm = 54 + Math.random();
      if (spm > 62) spm = 62 - Math.random();
      if (spmEl) spmEl.textContent = Math.round(spm);

      // L/R balance — sine oscillation between 48/52 and 52/48 (~14s period)
      lrPhase += 0.06;
      const lrL = Math.round(50 + Math.sin(lrPhase) * 2);
      const lrR = 100 - lrL;
      if (lrLEl) lrLEl.textContent = lrL;
      if (lrREl) lrREl.textContent = lrR;
      if (lrLBar) {
        lrLBar.style.flex  = '0 0 ' + lrL + '%';
        lrLBar.style.width = lrL + '%';
      }
      if (lrRBar) {
        lrRBar.style.flex  = '1 1 ' + lrR + '%';
        lrRBar.style.width = lrR + '%';
      }

      // Pace 1:28-1:36 correlated with efficiency
      const paceMin = 88;
      const paceMax = 96;
      const effPct = (effR - 45) / 9;
      const paceTot = paceMax - effPct * (paceMax - paceMin) + (Math.random() - 0.5) * 0.5;
      const m = Math.floor(paceTot / 60);
      const sFull = paceTot - m * 60;
      const sInt = Math.floor(sFull);
      if (paceEl) paceEl.textContent = m + ':' + sInt.toString().padStart(2, '0');

      // Distance accumulator
      const now = performance.now();
      const dt = Math.min((now - lastT) / 1000, 2);
      lastT = now;
      distM += (100 / paceTot) * dt;
      if (distEl) distEl.textContent = Math.round(distM).toLocaleString();
    }
    tick();
    // Gated: pauses when tab hidden / section off-screen / reduced-motion preferred
    setInterval(function() { if (shouldTick()) tick(); }, 900);
  })();

  /* BOTTOM MILESTONE — fires once when the user reaches the footer.
     Closes the journey: "+50 XP · LOOP DONE" with milestone styling. */
  (function() {
    if (window.innerWidth < 900) return;
    const foot = document.querySelector('.foot');
    if (!foot) return;
    let fired = false;
    const obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        if (fired) return;
        if (!e.isIntersecting || e.intersectionRatio < 0.20) return;
        fired = true;
        if (window.__zwimTrack) window.__zwimTrack('bottom_milestone_reached', {});
        if (window.__zwimTrack) window.__zwimTrack('xp_earned', { amount: 50, source: 'bottom_milestone' });
        if (typeof window.__zwimPopXp === 'function') {
          window.__zwimPopXp(50, '· LOOP DONE', 3500);
        }
        obs.disconnect();
      });
    }, { threshold: [0, 0.20, 0.5] });
    obs.observe(foot);
  })();

  /* LIVE SESSION TIMER — handled inside LIVE HUD B1.1 block above (single source of truth) */

  /* HUD LIFECYCLE — persistent across dark sections, hidden on light, docks into Leaderboard */
  (function() {
    const hud = document.querySelector('.hero__hud');
    const ldbSection = document.querySelector('.ldb');
    const youRow = document.querySelector('[data-ldb-you]');
    const ldbCard = document.querySelector('.ldb__card');
    if (!ldbSection || !youRow) return;

    // Defer the HUD reveal until Muller Next has loaded — prevents the "grow on load" flash
    // that happened when the system fallback rendered first then resized when the real font landed.
    function gateHudFonts(callback) {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(callback).catch(callback);
      } else {
        setTimeout(callback, 350);
      }
    }

    // ============ MOBILE PATH ============
    // No HUD card under 900px — dock has no source. Instead: reveal YOU row at rank 6
    // when the leaderboard card enters view, then let the existing climb logic take over.
    if (window.innerWidth < 900) {
      if (hud) hud.classList.remove('is-visible');
      let shown = false;
      const mobileObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(e) {
          if (!e.isIntersecting || e.intersectionRatio < 0.15) return;
          if (shown) return;
          shown = true;
          youRow.hidden = false;
          requestAnimationFrame(function() {
            youRow.classList.add('is-visible');
            youRow.classList.add('is-arriving');
            if (ldbCard) ldbCard.classList.add('has-you');
            // Count-up to rank-6 score then kick off the climb
            setTimeout(function() {
              youRow.classList.remove('is-arriving');
              const initialScore = 82000 + Math.floor(Math.random() * 600);
              const scoreEl = youRow.querySelector('[data-you-score]');
              const pwEl   = youRow.querySelector('[data-you-pw]');
              const paceEl = youRow.querySelector('[data-you-pace]');
              const effEl  = youRow.querySelector('[data-you-eff]');
              const start = performance.now();
              const dur = 1400;
              function easeOut(t) { return 1 - Math.pow(1 - t, 3); }
              function stepCount(now) {
                const t = Math.min((now - start) / dur, 1);
                const eT = easeOut(t);
                if (scoreEl) scoreEl.textContent = Math.round(eT * initialScore).toLocaleString();
                if (pwEl)    pwEl.textContent    = Math.round(eT * 52);
                if (effEl)   effEl.textContent   = Math.round(eT * 47) + '%';
                if (paceEl) {
                  const ps = eT * 92.3;
                  const m = Math.floor(ps / 60), s = Math.floor(ps - m * 60);
                  paceEl.textContent = m + ':' + s.toString().padStart(2, '0');
                }
                if (t < 1) requestAnimationFrame(stepCount);
              }
              requestAnimationFrame(stepCount);
              setTimeout(function() {
                if (window.__ldbStartYouClimb) window.__ldbStartYouClimb();
              }, 1500);
            }, 400);
          });
          mobileObs.disconnect();
        });
      }, { threshold: [0, 0.15, 0.3] });
      mobileObs.observe(ldbCard || ldbSection);
      return;
    }

    // ============ DESKTOP PATH (>=900px) — HUD floats, glides into leaderboard ============
    // Scroll-progress-driven dock. The HUD physically TRAVELS toward the YOU row position
    // as the user scrolls — no threshold trigger, no teleport. On a MacBook Air's small
    // viewport, the runway scales down proportionally (anchored on viewport height).
    if (!hud) return;

    const flowSvg = document.getElementById('zwim-flow');
    const flowPath = document.getElementById('zwim-flow-path');
    if (!flowPath) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ---- State
    let hasDocked      = false;   // sticky lock — set ONLY at progress >= 1.0
    let hasArrived     = false;   // arrival burst fires once
    let countUpStarted = false;   // score count-up fires once
    let youDomRevealed = false;   // YOU row hidden flag flipped once (then stays in DOM)
    let lastProgress   = -1;
    let ticking        = false;
    let sourceCaptured = false;
    let hudSourceCx = 0, hudSourceCy = 0;  // HUD center when no transform is applied

    function clamp(v, min, max) { return v < min ? min : v > max ? max : v; }
    function lerp(a, b, t) { return a + (b - a) * t; }
    function easeInOut(t)  { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

    function captureHudSource() {
      // Strip transform/opacity so we measure the HUD's untransformed position.
      const t = hud.style.transform;
      const o = hud.style.opacity;
      hud.style.transform = '';
      hud.style.opacity = '';
      const r = hud.getBoundingClientRect();
      hud.style.transform = t;
      hud.style.opacity = o;
      hudSourceCx = r.left + r.width / 2;
      hudSourceCy = r.top + r.height / 2;
      sourceCaptured = true;
    }

    function buildFlowPath(fromX, fromY, toX, toY) {
      // Quadratic bezier with arc biased away from direct line for organic feel.
      const midX = (fromX + toX) / 2;
      const midY = (fromY + toY) / 2;
      const dx = toX - fromX;
      const dy = toY - fromY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Perpendicular offset scaled to curve distance
      const nx = -dy / dist;
      const ny = dx / dist;
      const arc = Math.min(120, dist * 0.25);
      const cx = midX + nx * arc;
      const cy = midY + ny * arc;
      return 'M ' + fromX + ' ' + fromY + ' Q ' + cx + ' ' + cy + ' ' + toX + ' ' + toY;
    }

    function startScoreCountUp(targetScore) {
      if (countUpStarted) return;
      countUpStarted = true;
      const scoreEl = youRow.querySelector('[data-you-score]');
      const pwEl    = youRow.querySelector('[data-you-pw]');
      const paceEl  = youRow.querySelector('[data-you-pace]');
      const effEl   = youRow.querySelector('[data-you-eff]');
      if (!scoreEl) return;
      const start = performance.now();
      const duration = 1600;
      const initialHudPw  = parseInt(document.getElementById('hud-pw').textContent, 10) || 52;
      const initialHudEff = parseInt((document.getElementById('hud-wasted').textContent || '47').replace('%', ''), 10) || 47;
      function step(now) {
        const t = clamp((now - start) / duration, 0, 1);
        const e = easeOutCubic(t);
        scoreEl.textContent = Math.round(e * targetScore).toLocaleString();
        if (pwEl)  pwEl.textContent  = Math.round(e * initialHudPw);
        if (effEl) effEl.textContent = Math.round(e * initialHudEff) + '%';
        if (paceEl) {
          const paceSec = 60 + 32 + (Math.random() - 0.5);
          const targetSec = e * paceSec;
          const m = Math.floor(targetSec / 60);
          const s = Math.floor(targetSec - m * 60);
          paceEl.textContent = m + ':' + s.toString().padStart(2, '0');
        }
        if (t < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    function fireArrival() {
      if (hasArrived) return;
      hasArrived = true;
      youRow.classList.add('is-arriving');
      // Burst is a 0.9s CSS animation. After it plays, kick off count-up + leaderboard climb.
      setTimeout(function() {
        youRow.classList.remove('is-arriving');
        const initialScore = 82000 + Math.floor(Math.random() * 600);
        startScoreCountUp(initialScore);
        setTimeout(function() {
          if (window.__ldbStartYouClimb) window.__ldbStartYouClimb();
        }, 1800);
      }, 900);
    }

    function updateDock() {
      if (!sourceCaptured) { ticking = false; return; }
      const vh = window.innerHeight || 800;

      // ---- PROGRESS — scroll-driven, anchored on the leaderboard CARD's vertical CENTER.
      //   Runway start: card center is just below viewport bottom (vh + 80) — HUD floating.
      //   Runway end:   card center is in viewport center (vh / 2)        — HUD fully docked.
      //   Total scroll range = vh/2 + 80 px. Naturally short on MacBook Air, long on big monitors.
      //   This is the KEY change from the prior version: progress reflects how far the user
      //   has actually scrolled the leaderboard into view, not just whether an H2 hit a threshold.
      const cardEl = ldbCard || ldbSection;
      const cardRect = cardEl.getBoundingClientRect();
      const cardCenterY = cardRect.top + cardRect.height / 2;
      const startAnchor = vh + 80;
      const endAnchor   = vh / 2;
      const progress = clamp((startAnchor - cardCenterY) / (startAnchor - endAnchor), 0, 1);

      if (Math.abs(progress - lastProgress) < 0.003 && !hasDocked) { ticking = false; return; }
      lastProgress = progress;

      // ---- STICKY POST-DOCK — once locked at progress 1.0, never reverses.
      if (hasDocked) {
        hud.classList.add('is-docked');
        hud.classList.remove('is-docking');
        hud.style.transform = '';
        hud.style.opacity = '';
        flowSvg.classList.remove('is-active');
        flowSvg.style.opacity = '0';
        youRow.style.opacity = '';
        youRow.style.transform = '';
        if (!youRow.classList.contains('is-visible')) youRow.classList.add('is-visible');
        if (ldbCard) ldbCard.classList.add('has-you');
        ticking = false;
        return;
      }

      // ---- LOCK at progress 1.0 — fire arrival burst + count-up + climb
      if (progress >= 1.0) {
        hasDocked = true;
        if (window.__zwimTrack) window.__zwimTrack('leaderboard_dock_completed', {});
        hud.classList.add('is-docked');
        hud.classList.remove('is-docking');
        hud.style.transform = '';
        hud.style.opacity = '';
        flowSvg.classList.remove('is-active');
        flowSvg.style.opacity = '0';
        if (youRow.hidden) youRow.hidden = false;
        youRow.classList.add('is-visible');
        if (ldbCard) ldbCard.classList.add('has-you');
        youRow.style.opacity = '';
        youRow.style.transform = '';
        fireArrival();
        ticking = false;
        return;
      }

      // ---- TARGET — where the YOU row lives (or where it will land before reveal)
      let targetX, targetY;
      const youRect = youRow.getBoundingClientRect();
      if (youRow.offsetParent && youRect.height > 0) {
        targetX = youRect.left + youRect.width / 2;
        targetY = youRect.top + youRect.height / 2;
      } else if (ldbCard) {
        const c = ldbCard.getBoundingClientRect();
        targetX = c.left + c.width / 2;
        targetY = c.top + c.height * 0.55;  // roughly where rank-6 will sit
      } else {
        targetX = window.innerWidth / 2;
        targetY = vh / 2;
      }

      // ---- PHASE MAP (continuous lerp — every frame is interpolated, no jumps)
      //   [0.00 - 0.30]  HUD floats. User watches section approach.
      //   [0.20 - 0.55]  Flow line draws — cyan/pink trail from HUD to YOU position.
      //   [0.30 - 0.95]  HUD glides + scales toward YOU. CONTINUOUS, scroll-controlled.
      //   [0.55 - 0.95]  Cross-fade: HUD opacity 1->0 SIMULTANEOUSLY with YOU opacity 0->1.
      //                  Eye reads it as "HUD becomes the row".
      //   [0.55 - 0.75]  Flow line fades out (already drawn).
      //   [1.00]         Sticky lock. Arrival burst + count-up + leaderboard climb fire.

      // HUD travel + fade (translate + scale + opacity all interpolated continuously)
      if (progress >= 0.30 && !prefersReduced) {
        hud.classList.add('is-docking');
        const moveT = easeInOut(clamp((progress - 0.30) / 0.65, 0, 1));
        const tx = lerp(0, targetX - hudSourceCx, moveT);
        const ty = lerp(0, targetY - hudSourceCy, moveT);
        const sc = lerp(1, 0.32, moveT);
        hud.style.transform = 'translate(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px) scale(' + sc.toFixed(3) + ')';
        const fadeT = clamp((progress - 0.55) / 0.40, 0, 1);
        hud.style.opacity = (1 - fadeT).toFixed(3);
      } else {
        hud.classList.remove('is-docking');
        hud.style.transform = '';
        hud.style.opacity = '';
      }

      // Flow line — draws from HUD source center to YOU target, then fades out
      if (progress >= 0.20 && progress < 0.75 && !prefersReduced) {
        flowSvg.classList.add('is-active');
        flowSvg.setAttribute('width', window.innerWidth);
        flowSvg.setAttribute('height', vh);
        flowSvg.setAttribute('viewBox', '0 0 ' + window.innerWidth + ' ' + vh);
        flowPath.setAttribute('d', buildFlowPath(hudSourceCx, hudSourceCy, targetX, targetY));
        const drawT = clamp((progress - 0.20) / 0.35, 0, 1);
        flowPath.style.strokeDashoffset = (1 - drawT).toFixed(3);
        const fadeT = clamp((progress - 0.55) / 0.20, 0, 1);
        flowSvg.style.opacity = (1 - fadeT).toFixed(3);
      } else {
        flowSvg.classList.remove('is-active');
        flowSvg.style.opacity = '0';
      }

      // YOU row cross-fade — opacity + transform interpolated continuously, mirrors HUD fadeout
      if (progress >= 0.55) {
        if (!youDomRevealed) {
          youDomRevealed = true;
          youRow.hidden = false;
          requestAnimationFrame(function() {
            youRow.classList.add('is-visible');
            if (ldbCard) ldbCard.classList.add('has-you');
          });
        }
        const youT = clamp((progress - 0.55) / 0.40, 0, 1);
        const eased = easeOutCubic(youT);
        youRow.style.opacity = eased.toFixed(3);
        const ty = lerp(20, 0, eased);
        const sc = lerp(0.96, 1, eased);
        youRow.style.transform = 'translateY(' + ty.toFixed(1) + 'px) scale(' + sc.toFixed(3) + ')';
      } else if (youDomRevealed) {
        // User scrolled back UP — keep YOU in DOM but invisible (no layout shift on re-entry)
        youRow.style.opacity = '0';
        youRow.style.transform = 'translateY(20px) scale(0.96)';
      }

      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateDock);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function() {
      // Recapture HUD source on resize since viewport width changes its right-anchored position
      sourceCaptured = false;
      captureHudSource();
      onScroll();
    }, { passive: true });

    // Initialize: wait for fonts so HUD's measured size is final, then capture source + run.
    gateHudFonts(function() {
      hud.classList.add('is-visible');
      // Allow one frame for the visibility transition before measuring the source.
      requestAnimationFrame(function() {
        captureHudSource();
        updateDock();
      });
    });
  })();

  /* LIVE SYNC — HUD's PW/Pace/Efficiency mirror into YOU row meta line (NOT the score — climbYou owns that) */
  (function() {
    const hudPw = document.getElementById('hud-pw');
    if (!hudPw) return;
    const pwEl = document.querySelector('[data-you-pw]');
    const paceEl = document.querySelector('[data-you-pace]');
    const effEl = document.querySelector('[data-you-eff]');
    if (!pwEl) return;
    setInterval(function() {
      const row = document.querySelector('[data-ldb-you]');
      if (!row || row.hidden || !row.classList.contains('is-visible')) return;
      const pw = parseInt(hudPw.textContent, 10) || 52;
      const eff = parseInt((document.getElementById('hud-wasted').textContent || '47%').replace('%', ''), 10) || 47;
      const paceM = document.getElementById('hud-pace-m').textContent || '1';
      const paceS = document.getElementById('hud-pace-s').textContent || '32';
      // Locked pace format rule: m:ss /100m. NO decimals. Drop the tenths digit
      // so the YOU row reads "1:32 /100m" not "1:32.3 /100". Per Victor 8 May 2026.
      const paceSPad = (paceS.length < 2) ? ('0' + paceS) : paceS;
      if (pwEl)   pwEl.textContent   = pw;
      if (effEl)  effEl.textContent  = eff + '%';
      if (paceEl) paceEl.textContent = paceM + ':' + paceSPad;
    }, 1100);
  })();

  /* LEADERBOARD TICKER + YOU CLIMB */
  (function() {
    var card = document.querySelector('.ldb__card');
    if (!card) return;
    var rivals = Array.from(card.querySelectorAll('[data-ldb-row]'));
    var youRow = card.querySelector('[data-ldb-you]');
    if (!rivals.length) return;

    // 6-position rank map (with YOU inserted, 6 competitors total)
    var rankDisplay = [
      { medal: '🏆', num: '' },
      { medal: '🥈', num: '' },
      { medal: '🥉', num: '' },
      { medal: '', num: '#4' },
      { medal: '', num: '#5' },
      { medal: '', num: '#6' }
    ];

    function allRankedRows() {
      // DOM-order array of rows participating in ranking (rivals + YOU if visible), excludes placeholder
      var nodes = Array.from(card.querySelectorAll('[data-ldb-row], [data-ldb-you]'));
      return nodes.filter(function(r) {
        if (r.hasAttribute('hidden')) return false;
        if (r.classList.contains('ldb__row--user')) return false;
        return true;
      });
    }

    function getScore(row) {
      var el = row.querySelector('.ldb__pw-n') || row.querySelector('[data-you-score]');
      if (!el) return 0;
      return parseInt(el.textContent.replace(/,/g, ''), 10) || 0;
    }
    function setScore(row, val) {
      var el = row.querySelector('.ldb__pw-n') || row.querySelector('[data-you-score]');
      if (el) el.textContent = val.toLocaleString();
    }

    function updateRanks() {
      var ordered = allRankedRows();
      ordered.forEach(function(row, i) {
        if (i >= rankDisplay.length) return;
        var isYou = row.hasAttribute('data-ldb-you');
        var medalEl = row.querySelector('.ldb__medal');
        var numEl = isYou ? row.querySelector('[data-you-rank]') : row.querySelector('.ldb__rank-num');
        // Default from rankDisplay
        var medal = rankDisplay[i].medal;
        var num   = rankDisplay[i].num;
        if (medalEl) {
          medalEl.textContent = medal;
          medalEl.style.display = medal ? '' : 'none';
        }
        if (numEl) {
          numEl.textContent = num;
          numEl.style.display = num ? '' : 'none';
        }
      });
    }
    // Exposed so the dock animation can re-rank after YOU appears
    window.__ldbUpdateRanks = updateRanks;

    function animateSwap(a, b, callback) {
      var aRect = a.getBoundingClientRect();
      var bRect = b.getBoundingClientRect();
      var dy = bRect.top - aRect.top;
      a.style.transition = 'transform 0.55s cubic-bezier(.22,1,.36,1)';
      b.style.transition = 'transform 0.55s cubic-bezier(.22,1,.36,1)';
      a.style.transform = 'translateY(' + dy + 'px)';
      b.style.transform = 'translateY(' + (-dy) + 'px)';
      setTimeout(function() {
        a.style.transition = ''; a.style.transform = '';
        b.style.transition = ''; b.style.transform = '';
        a.parentNode.insertBefore(b, a);
        updateRanks();
        if (callback) callback();
      }, 580);
    }

    var swapping = false;

    // Rival nudges — small, infrequent (keeps the board "alive" without disrupting YOU's climb)
    function nudgeRival() {
      if (swapping) return;
      var idx = Math.floor(Math.random() * rivals.length);
      var row = rivals[idx];
      var nEl = row.querySelector('.ldb__pw-n');
      var tick = row.querySelector('.ldb__pw-tick');
      if (!nEl || !tick) return;
      var delta = Math.floor(Math.random() * 3) + 1;
      var cur = parseInt(nEl.textContent.replace(/,/g, ''), 10);
      if (isNaN(cur)) return;
      var newVal = cur + delta;
      tick.textContent = '+' + delta;
      tick.classList.add('show');
      setTimeout(function() {
        nEl.textContent = newVal.toLocaleString();
        tick.classList.remove('show');
        // Rival-vs-rival swap check
        var all = allRankedRows();
        var curIdx = all.indexOf(row);
        if (curIdx > 0) {
          var above = all[curIdx - 1];
          // Never swap a rival PAST YOU (protect YOU's climbed rank)
          if (above.hasAttribute('data-ldb-you')) return;
          if (newVal > getScore(above)) {
            swapping = true;
            animateSwap(above, row, function() { swapping = false; });
          }
        }
      }, 400);
    }
    setInterval(nudgeRival, 5500);

    // YOU climb — triggered after the dock+count-up completes. Target: rank 2 @ 96,732.
    var FINAL_SCORE = 96732;
    var TARGET_RANK = 2;  // stop climbing once at rank 2 (below Marcus at 98,765, above Sophie at 94,260)
    var climbInterval = null;
    var stabilized = false;

    function climbYou() {
      if (swapping) return;
      if (!youRow || youRow.hidden) return;
      var all = allRankedRows();
      var rank = all.indexOf(youRow) + 1;
      var cur = getScore(youRow);

      if (rank <= TARGET_RANK) {
        // Stabilize near the target score with tiny live fluctuations (±50 around FINAL_SCORE)
        if (!stabilized) {
          stabilized = true;
          setScore(youRow, FINAL_SCORE);
          var tickFinal = youRow.querySelector('[data-you-tick]');
          if (tickFinal) tickFinal.textContent = '0';
          clearInterval(climbInterval);
          climbInterval = setInterval(climbYou, 3800);
          // Final arrival sparkle
          var scoreV = youRow.querySelector('.ldb__score-v');
          if (scoreV) {
            scoreV.classList.add('is-pulse');
            setTimeout(function() { scoreV.classList.remove('is-pulse'); }, 700);
          }
          return;
        }
        // Gentle drift ±80 around FINAL_SCORE
        var drift = Math.floor((Math.random() - 0.5) * 160);
        var driftVal = FINAL_SCORE + drift;
        setScore(youRow, driftVal);
        var tickRest = youRow.querySelector('[data-you-tick]');
        if (tickRest) tickRest.textContent = drift >= 0 ? drift : '-' + Math.abs(drift);
        return;
      }

      // Still below target rank — calculate surge size based on next rival's score
      var above = all[all.indexOf(youRow) - 1];
      var aboveScore = above ? getScore(above) : 0;
      var gap = aboveScore - cur;
      // Overshoot the gap by 300-1200 so the swap definitely triggers
      var delta = Math.max(400, gap + 300 + Math.floor(Math.random() * 900));
      // Cap delta so score feels realistic
      if (delta > 3500) delta = 2200 + Math.floor(Math.random() * 1200);
      var newVal = cur + delta;

      var tickEl = youRow.querySelector('[data-you-tick]');
      if (tickEl) tickEl.textContent = delta;
      setScore(youRow, newVal);

      var scoreV = youRow.querySelector('.ldb__score-v');
      if (scoreV) {
        scoreV.classList.add('is-pulse');
        setTimeout(function() { scoreV.classList.remove('is-pulse'); }, 700);
      }

      setTimeout(function() {
        var all2 = allRankedRows();
        var youIdx = all2.indexOf(youRow);
        if (youIdx > 0) {
          var above2 = all2[youIdx - 1];
          if (getScore(youRow) > getScore(above2)) {
            swapping = true;
            animateSwap(above2, youRow, function() { swapping = false; });
          }
        }
      }, 400);
    }

    // Expose start hook for the HUD dock logic to call once count-up completes
    window.__ldbStartYouClimb = function() {
      if (climbInterval) return;  // already running
      stabilized = false;
      updateRanks();
      // Fast climb cadence — 1.6s between surges = full 6→2 climb in ~8 seconds
      climbInterval = setInterval(climbYou, 1600);
    };
  })();

    /* EXP CARD SCROLL DRIFT */
  (function() {
    const card = document.querySelector('.exp__card');
    if (!card) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    card.style.willChange = 'transform';
    let ticking = false;
    function update() {
      const r = card.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      if (r.bottom < -200 || r.top > vh + 200) return;
      const center = r.top + r.height / 2;
      const prog = (center - vh / 2) / (vh / 2 + r.height / 2);
      const ty = -prog * 28;
      card.style.transform = 'translate3d(0,' + ty.toFixed(2) + 'px,0)';
      ticking = false;
    }
    window.addEventListener('scroll', function() {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  })();

  
  /* PROOF CAROUSEL DOTS */
  (function() {
    var carousel = document.getElementById('proof-carousel');
    var dotsWrap = document.getElementById('proof-dots');
    if (!carousel || !dotsWrap) return;
    var cards = carousel.querySelectorAll('.proof__rev');
    var numDots = cards.length;
    for (var i = 0; i < numDots; i++) {
      var dot = document.createElement('span');
      dot.className = 'proof__dot' + (i === 0 ? ' active' : '');
      dotsWrap.appendChild(dot);
    }
    var dots = dotsWrap.querySelectorAll('.proof__dot');
    function updateDots() {
      var scrollLeft = carousel.scrollLeft;
      var cardWidth = 356;
      var idx = Math.round(scrollLeft / cardWidth);
      idx = Math.max(0, Math.min(idx, numDots - 1));
      dots.forEach(function(d, i) {
        d.classList.toggle('active', i === idx);
      });
    }
    carousel.addEventListener('scroll', updateDots, { passive: true });
  })();
  /* BPD WEEK DOTS ANIMATE IN */
  (function() {
    const days = document.querySelectorAll('.bpd__day');
    if (!days.length) return;
    const obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-vis');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.5 });
    days.forEach(function(d) { obs.observe(d); });
  })();

  /* SCROLL DEPTH TRACKING — fires zwim:track at 25/50/75/100% page scroll depth.
     Each tier fires once per session. Throttled via requestAnimationFrame. */
  (function() {
    if (!window.__zwimTrack) return;
    const tiers = [25, 50, 75, 100];
    const fired = {};
    let ticking = false;
    function check() {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) { ticking = false; return; }
      const pct = Math.min(100, Math.round((window.scrollY / docH) * 100));
      tiers.forEach(function(t) {
        if (!fired[t] && pct >= t) {
          fired[t] = true;
          window.__zwimTrack('scroll_depth_' + t, { pct: pct });
        }
      });
      ticking = false;
    }
    window.addEventListener('scroll', function() {
      if (!ticking) { requestAnimationFrame(check); ticking = true; }
    }, { passive: true });
    check();
  })();
})();
